/* DoctorCaseDetail — case-scoped patient view.
 *
 * The doctor's mental model: opening a case is opening "this patient's
 * current case". So the page leads with the same patient header as the
 * Patient Profile, then the clinical pathway, then the plan from the
 * Center of Excellence (with Approve / Request revision actions right
 * above it). Source records (STL, intra-oral) live on the patient
 * profile — this page is purely for plan review.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import { CaseInfoChipRow } from "@/components/shared/CaseInfoChipRow";
import DoctorCaseCommandHeader from "@/components/doctor/DoctorCaseCommandHeader";
import DoctorWorkspaceDeck from "@/components/doctor/DoctorWorkspaceDeck";


import {
    CaretLeft, ArrowUUpLeft, CheckCircle, FileArrowUp,
    Sparkle, ClipboardText, CaretUp, CaretDown,
    GitDiff, X as CloseIcon, ChatCircleDots, NotePencil,
    ClockClockwise, ArrowRight, Tooth, Factory, Lock,
    ArrowsClockwise, Prescription, FilePdf,
} from "@phosphor-icons/react";
import PlanPanel from "@/components/case/PlanPanel";
import PlanSummary from "@/components/case/PlanSummary";
import DoctorSelfPlanView from "@/components/case/DoctorSelfPlanView";
import CaseAppointments from "@/components/case/CaseAppointments";
import { MaterialsTraceabilityPanel } from "@/components/case/MaterialsTraceabilityPanel";
import LabProductionTab from "@/components/case/LabProductionTab";
import WorkflowTab from "@/components/case/WorkflowTab";
import { LinkedCasesPanel } from "@/components/case/LinkedCasesPanel";
import PlanIterationsStack from "@/components/case/PlanIterationsStack";
import PlanApprovalHero from "@/components/case/PlanApprovalHero";
import PlannerResponseHero from "@/components/case/PlannerResponseHero";
import CaseHistoryTab from "@/components/case/CaseHistoryTab";
import PlanCompareTab from "@/components/case/PlanCompareTab";
import DoctorWearScheduleReview from "@/components/case/DoctorWearScheduleReview";
import WearScheduleSection from "@/components/case/WearScheduleSection";
import CaseTimelineTab from "@/components/case/CaseTimelineTab";
import CaseChatThread from "@/components/case/CaseChatThread";
import PatientNotesSection from "@/components/patient/PatientNotesSection";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
// iter98.99v — EditSubmissionDialog import removed; Edit navigates to wizard
// iter101.89 — CancelCaseDialog + CasePdfMenu no longer mounted from the
// case header. Both imports dropped (unused). If a future overflow menu
// needs to re-open cancel-case, re-add the import + mount pattern from
// git history (search "case-header-cancel-case").
import CaseRecordsManager from "@/components/case/CaseRecordsManager";
import SectionCard from "@/components/case/SectionCard";
import ScanVisitCaseView from "@/components/case/ScanVisitCaseView";
import ActivateTreatmentDialog from "@/components/case/ActivateTreatmentDialog";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import { statusLabel as sharedStatusLabel, statusTone as sharedStatusTone } from "@/lib/caseStatus";

/* iter101.0 — Status labels are now sourced from `/lib/caseStatus.js`
   so every doctor surface reads the same string. Only the tone
   mapping stays local because this file uses the smaller 3-token
   palette (primary / amber / neutral). */
const SHARED_TONE_TO_LOCAL = {
    slate:   "neutral",
    sky:     "primary",
    violet:  "primary",
    emerald: "primary",
    amber:   "amber",
    rose:    "neutral",
};
function statusMetaFor(status) {
    const label = sharedStatusLabel(status, "doctor");
    const tone = SHARED_TONE_TO_LOCAL[sharedStatusTone(status)] || "neutral";
    return { label: label || (status || "—"), tone };
}

const STAGE_PILL = {
    primary: "bg-primary/10 text-primary border-primary/20",
    amber:   "bg-amber-100 text-amber-800 border-amber-200",
    neutral: "bg-foreground/5 text-foreground/80 border-foreground/15",
};

/* COE-planned pathway: the user-facing stages the doctor cares about.
   Internal QA review was removed (QA is now a non-blocking sampling pool,
   not a gating step). Routing + planning are merged into a single
   "Planning" stage to reduce noise. */
const PATHWAY_STAGES_COE = [
    { id: "submitted",        label: "Submitted",            statuses: ["submitted"] },
    { id: "planning",         label: "Planning",             statuses: ["assigned_to_planner", "in_planning", "info_requested", "submitted_to_qa"] },
    { id: "ready",            label: "Plan ready for you",   statuses: ["sent_for_approval", "ready_for_review"] },
    { id: "production",       label: "Production",           statuses: ["approved", "assigned_to_production", "in_production"] },
    { id: "shipped",          label: "Aligners Shipped",     statuses: ["shipped"] },
    { id: "in_treatment",     label: "In Treatment",         statuses: ["in_treatment", "treatment_started", "refinement_requested"] },
    { id: "completed",        label: "Completed",            statuses: ["completed"] },
];

/* Doctor-planned pathway: doctor authors → straight to lab. Half the stages
   because there's no planner + QA round-trip. Shorter visual = correct
   shorter perceived flow. */
const PATHWAY_STAGES_DOCTOR = [
    { id: "draft",                    label: "Draft",                statuses: ["draft"] },
    { id: "plan_authoring",           label: "Authoring plan",       statuses: ["plan_authoring"] },
    { id: "submitted_to_production",  label: "Sent to lab",          statuses: ["submitted_to_production"] },
    { id: "in_production",            label: "Production Started",   statuses: ["in_production"] },
    { id: "shipped",                  label: "Aligners Shipped",     statuses: ["shipped"] },
    { id: "in_treatment",             label: "Aligners Received",    statuses: ["in_treatment"] },
    { id: "treatment_started",        label: "Treatment Started",    statuses: ["treatment_started"] },
    { id: "completed",                label: "Completed",            statuses: ["completed"] },
];

function pathwayStagesFor(mode) {
    return (mode === "doctor") ? PATHWAY_STAGES_DOCTOR : PATHWAY_STAGES_COE;
}

function pathwayIndex(status, mode) {
    return pathwayStagesFor(mode).findIndex((s) => s.statuses.includes(status));
}

const initials = (name) =>
    (name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso)
            .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
            .replace(/ /g, "-");
    } catch { return iso; }
};

function humanPatientId(p) {
    if (!p) return "—";
    return p.human_id || `INV-PT-${(p.id || "").slice(0, 6).toUpperCase()}`;
}

export default function DoctorCaseDetail() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canApprove = user?.role === "doctor";  // clinic_staff cannot approve
    const [c, setC] = useState(null);
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // iter98.4 — Activation dialog state. Lives on Case Detail so the
    // doctor can fit aligners from the same context they're working in.
    const [activateOpen, setActivateOpen] = useState(false);
    /* iter98.77 — premium "Planner has your reply" dialog shown after
     * the doctor responds to a Hold & query. Symmetric to the planner's
     * planner_query_to_doctor amber dialog so both portals share the
     * same "you unblocked the loop" reward. */
    const [resumeOpen, setResumeOpen] = useState(false);
    /* iter98.99i — Premium "Plan approved → routed to production"
     * dialog. Fires when the doctor signs off on the planner-authored
     * treatment plan. Closing it triggers the case reload so the new
     * status (assigned_to_production / awaiting_production_assignment)
     * is reflected in the page. */
    const [approvedOpen, setApprovedOpen] = useState(false);
    /* Fetch case + patient in parallel. The patient call mirrors the data
       used by the Patient Profile page so the header looks identical. */
    const load = async () => {
        setLoading(true);
        try {
            const caseResp = await api.get(`/cases/${caseId}`);
            setC(caseResp.data);
            setError(null);
            if (caseResp.data?.patient_id) {
                try {
                    const pResp = await api.get(`/doctor/patients/${caseResp.data.patient_id}`);
                    setPatient(pResp.data);
                } catch {
                    /* Patient lookup is informational — if it fails we still
                       render the case body using the case-level fields. */
                }
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to load case");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [caseId]);

    /* Hook order matters: dialogs declared before any early-return branches
       below so React always sees the same hook count.
       iter101.89 — CancelCaseDialog no longer mounted from the header
       (Cancel button was removed per user direction). The `cancelOpen`
       state + dialog are kept commented out for reference in case a
       future overflow-menu re-introduces the action. */
    // const [cancelOpen, setCancelOpen] = useState(false);

    if (loading && !c) {
        return <div className="text-muted-foreground py-16 text-center" data-testid="doctor-case-loading">Loading case…</div>;
    }
    if (error || !c) {
        return (
            <div className="space-y-4" data-testid="doctor-case-error">
                <Link to="/doctor/cases" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
                    <CaretLeft size={12} weight="bold" /> Back to Aligner Cases
                </Link>
                <div className="bg-card border border-rose-200 rounded-2xl p-8 text-center text-rose-700">
                    {error || "Case not found"}
                </div>
            </div>
        );
    }

    const status = statusMetaFor(c.status);
    const pillCls = STAGE_PILL[status.tone];
    const setupFiles = (c.files || []).filter((f) => f.kind === "setup" || f.kind === "setup_plan");

    // Workflow handlers — operational status transitions (in_production /
    // shipped) belong to admin/COE ops, blocked at the API. The doctor's
    // clinical transitions (mark in_treatment, mark completed) flow through
    // the standalone AlignerStageTracker card.
    // iter98.99v — `submitCase` removed. Doctors submit cases from
    // inside the wizard (POST /cases/{id}/submit fires on the final
    // wizard step), NOT from the case detail page. The case detail
    // page is read-only for draft state — Edit jumps back into the
    // wizard which is the single source of truth for submission.
    const approveCase = async (confirmedRead = false) => {
        try {
            await api.put(`/cases/${c.id}/approve`, { confirmed_read: !!confirmedRead });
            /* iter98.99i — Replace the thin sonner toast with the premium
             * "Plan approved → routed to production" CaseSubmittedDialog.
             * Symmetric pair to the planner-side `plan_sent_to_doctor`.
             * Reload is deferred to dialog dismissal so the page reflects
             * the new status when the doctor returns to it. */
            setApprovedOpen(true);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed to approve"); }
    };
    const resubmitAfterInfo = async (responseNote = "") => {
        try {
            await api.put(`/cases/${c.id}/resubmit-after-info`, { response_note: responseNote || null });
            /* iter98.77 — replace the thin toast with the premium
             * emerald "case resumed" dialog. Mirrors the symmetry of
             * the planner's amber Hold & query dialog so both sides
             * see the same loop-closed celebration. Reload happens on
             * dialog dismissal so the page reflects the new status. */
            setResumeOpen(true);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    /* iter101.89 — `editAllowed` + `cancelAllowed` gating removed. Both
       actions were dropped from the header per user direction. Doctors
       resume drafts directly from the drafts list (pencil action-icon
       on the row); cancel-case is deferred until we surface it via an
       overflow menu on the case detail body. */

    return (
        <div className="space-y-6" data-testid="doctor-case-detail">
            {/* BREADCRUMB + LAB CHAT JUMP */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <Link to="/doctor/cases" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="doctor-case-back">
                    <CaretLeft size={12} weight="bold" /> Back to Aligner Cases
                </Link>
                <div className="flex items-center gap-3">
                    {/* iter98.99am — View Rx quick-link. Visible once the
                        doctor has signed the prescription (always true
                        post-submission since Sign+Submit is one click).
                        Opens the signed Rx PDF in a new tab so the
                        doctor can review what they committed to without
                        digging into the case PDF dropdown.
                        iter98.99dd — "Open in Lab Chat" link dropped per
                        user cleanup direction; the Lab Chat tab is
                        already in the tab nav below. */}
                    {c?.prescription_compliance?.signed_at && (
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const resp = await api.get(`/cases/${c.id}/prescription/pdf`, { responseType: "blob" });
                                    const url = URL.createObjectURL(resp.data);
                                    window.open(url, "_blank");
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Couldn't load the Rx PDF");
                                }
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            data-testid="case-detail-view-rx"
                        >
                            <Prescription size={13} weight="duotone" /> View Rx
                        </button>
                    )}
                </div>
            </div>

            {/* PATIENT HEADER — iter101.59 · shared "command bar" design
                (see /components/shared/CommandHeaderShell). Doctors and
                planners now see the same editorial identity band + chip
                strip on the same case. */}
            <DoctorCaseCommandHeader
                patient={patient || {
                    id: c.patient_id,
                    name: c.patient_name,
                    phone: c.patient_phone,
                    // iter78 — surface the photo even on the lightweight
                    // fallback patient shape, so the avatar doesn't
                    // momentarily disappear during the patient-profile
                    // hydration round-trip.
                    photo_data_url: c.patient_photo_data_url,
                }}
                caseObj={c}
                clinicName={c.practice_location_name || c.clinic_name || patient?.primary_clinic_name}
                caseStatusKey={c.status}
                caseCode={c.case_code || c.code}
                caseId={c.id}
                onSubmit={null}
                onResubmitAfterInfo={c.status === "info_requested" ? resubmitAfterInfo : null}
                onConfirmedReceipt={c.status === "shipped" ? load : null}
                shippingCarrier={c.shipping_carrier}
                trackingNumber={c.tracking_number}
                onComplianceChosen={load}
                humanPatientId={humanPatientId}
            />

            {/* iter98.99v — EditSubmissionDialog removed. Edit submission
                now navigates the doctor back into the case-submission
                wizard at /doctor/cases/new?draft={caseId} so the wizard
                is the single source of truth for both authoring + final
                submission. */}

            {/* iter101.89 — Cancel case dialog no longer mounted here.
                The Cancel button that used to open it was removed from
                the case header. Kept the CancelCaseDialog import for
                easy re-mount from a future overflow menu. */}

            {/* iter98.4 — "Fit aligners & start treatment" activation
                dialog. The banner below opens this; the dialog itself
                handles adaptive defaults (walk-in vs. online-booker)
                and the WhatsApp invite flow. */}
            <ActivateTreatmentDialog
                open={activateOpen}
                onOpenChange={setActivateOpen}
                caseId={c.id}
                onActivated={load}
            />

            {/* iter98.77 — Premium "Planner has your reply" dialog.
                Shown after resubmitAfterInfo succeeds; closing it
                reloads the case so the new status pill is reflected. */}
            <CaseSubmittedDialog
                open={resumeOpen}
                flow="doctor_resume_after_query"
                caseCode={c?.code || ""}
                primaryLabel="View case"
                secondaryLabel="Back to dashboard"
                onPrimary={() => { setResumeOpen(false); load(); }}
                onSecondary={() => { setResumeOpen(false); navigate("/doctor"); }}
            />

            {/* iter98.99i — Premium "Plan approved → routed to production"
                dialog. Symmetric pair to the planner-side `plan_sent_to_doctor`.
                Primary CTA stays on the case (load() refreshes the status
                pill + activity); secondary returns to the dashboard. */}
            <CaseSubmittedDialog
                open={approvedOpen}
                flow="doctor_approved_plan"
                caseCode={c?.code || ""}
                onPrimary={() => { setApprovedOpen(false); load(); }}
                onSecondary={() => { setApprovedOpen(false); navigate("/doctor"); }}
            />

            {/* iter98.4 — Activation banner. Shows ONLY when the case is
                in the right state to be activated (aligners received +
                not yet activated). The banner self-hides after the
                doctor activates — the regular case-detail flow takes
                over. */}
            {!c.treatment_activated_at && ["in_treatment", "treatment_started", "delivered", "shipped"].includes(c.status) && (
                <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50/40 to-transparent p-4 sm:p-5" data-testid="case-detail-activation-banner">
                    <div className="flex items-center gap-4 flex-wrap">
                        <span className="w-10 h-10 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
                            <Sparkle size={18} weight="bold" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-sm font-bold leading-tight">Ready to fit aligners?</div>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                Once you&apos;ve fitted the aligners, activate the patient&apos;s dashboard so they can track wear-time and switch aligners themselves.
                            </p>
                        </div>
                        <Button
                            onClick={() => setActivateOpen(true)}
                            className="rounded-full h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                            data-testid="case-detail-activate-btn"
                        >
                            <Sparkle size={14} weight="bold" className="mr-1.5" />
                            Fit aligners & activate
                        </Button>
                    </div>
                </div>
            )}

            {/* iter97.88 — Pre-clinical-data lifecycle: when a case is in
                `scan_visit_scheduled` or `scan_visit_completed`, there's
                almost no clinical record yet, so the full DoctorCaseTabs
                shell (Treatment Plan, Compare, Lab Chat, etc.) is just
                noise. Render a focused scan-visit view INSTEAD of the
                regular tabs. The DoctorCaseCommandHeader above still
                renders so the doctor has context + the case code. */}
            {/* iter100.78 — Cancelled = terminal state. The entire clinical
                surface (clinical pathway, submission, treatment plan, notes,
                lab chat, timeline) is hidden behind a single full-page
                "Case Cancelled" overlay. The patient header + back link
                stay so the doctor still has context and an escape path. */}
            {c.status === "cancelled" ? (
                <CancelledCaseOverlay c={c} />
            ) : (c.status === "scan_visit_scheduled" || c.status === "scan_visit_completed") ? (
                <ScanVisitCaseView
                    caseRecord={c}
                    patient={c.patient}
                    onChanged={load}
                />
            ) : (
                <>
                    {/* INFO-REQUEST BANNER — when the planner has paused the case
                        asking for more info, surface their exact question prominently
                        at the top of the page. Without this the doctor opens the case
                        from the notification bell and sees the "Needs your input" pill
                        but not the actual question, forcing them to either remember
                        the bell preview or hunt for it in the Timeline tab. */}
                    {c.status === "info_requested" && c.info_request_note && (
                        <InfoRequestBanner
                            note={c.info_request_note}
                            onResubmit={resubmitAfterInfo}
                            onOpenChat={() => window.location.href = `/doctor/lab-chat?case=${c.id}`}
                        />
                    )}

                    {/* iter97.11 — Case-locked banner. Shown when the case has reached
                        a terminal state (completed / cancelled) and the clinical
                        contract is frozen. Doctor still has Lab Chat / Notes /
                        progress-photo uploads / refinement-spawn actions open — the
                        banner makes the boundary crystal clear so they don't try to
                        edit the plan and hit a confusing 423 error. */}
                    {c.is_locked && (
                        <CaseLockedBanner
                            reason={c.locked_reason}
                            status={c.status}
                            onOpenChat={() => window.location.href = `/doctor/lab-chat?case=${c.id}`}
                        />
                    )}

                    {/* CLINICAL PATHWAY — placed above the brief so the doctor's eye
                        lands on "where am I in this case?" first, then reads the
                        conversation that follows. */}
                    <ClinicalPathwayCompact currentStatus={c.status} mode={c.planning_mode} />

                    {/* The case body lives in tabs (Submission · Treatment Plan ·
                        Compare · Lab Chat · Notes · Timeline). Action CTAs (submit,
                        resubmit-after-info, approve, request revision, confirm
                        receipt, recall) live where the doctor naturally takes them —
                        inline on the Patient Header (Recall) or on the active plan
                        iteration card (Approve/Request revision) or on the
                        lifecycle-specific empty states inside the tabs themselves. */}
                    <DoctorCaseTabs
                        c={c}
                        user={user}
                        setupFiles={setupFiles}
                        canApprove={canApprove}
                        onApprove={approveCase}
                        onChange={load}
                    />
                </>
            )}
        </div>
    );
}


/* ─── DoctorCaseTabs — Treatment | Submission | Treatment Plan (w/ inline Compare) | Lab & Production | Notes | Lab Chat | Timeline ─── */
function DoctorCaseTabs({ c, user, setupFiles, canApprove, onApprove, onChange }) {
    const navigate = useNavigate();
    const [tab, setTab] = useState(() => {
        try {
            const v = new URLSearchParams(window.location.search).get("tab");
            // iter79 — `compare` is no longer a top-level tab (it's an inline
            // toggle on the Plan tab). Old bookmarks degrade gracefully —
            // land on Plan with compare auto-opened. Notes remains a
            // top-level tab (iter80 — user preferred the original tab
            // pattern over the slide-over drawer).
            if (v === "compare") return "plan";
            // iter83 — `compare` / `treatment` / `workflow` / `production` all
            // degrade to the new `overview` / `shipments` tab names where
            // applicable. The Compare inline toggle on Plan tab catches
            // the `compare` deeplink.
            if (v === "compare") return "plan";
            // Legacy aliases — all flow into the new "overview" tab.
            if (v === "treatment" || v === "workflow") return "overview";
            // iter84 — Production renamed to Shipments. Old links still work.
            if (v === "production") return "shipments";
            if (["submission", "plan", "history", "chat", "notes", "timeline", "overview", "shipments"].includes(v)) return v;
        } catch { /* noop */ }
        // Default landing depends on what the doctor needs to act on:
        //  - Plan-approval cases land on the Plan tab.
        //  - Anything past plan-approval lands on the Overview tab —
        //    that's the doctor's daily action hub (iter84 rename).
        //  - Everything else lands on Submission.
        if (["sent_for_approval", "ready_for_review"].includes(c.status)) return "plan";
        if (["approved", "in_production", "shipped", "delivered", "aligners_received",
             "in_treatment", "treatment_started", "refinement_requested", "completed"].includes(c.status)) return "overview";
        return "submission";
    });

    /* iter79 — Compare iterations is an inline toggle on the Plan tab
       instead of a separate top-level tab. Opens via the toggle in the
       Plan header; honours `?tab=compare` deeplinks too. */
    const [compareOpen, setCompareOpen] = useState(() => {
        try { return new URLSearchParams(window.location.search).get("tab") === "compare"; }
        catch { return false; }
    });

    /* Sync tab → URL so back-button + share-link work as expected.
       iter79 — compareOpen is a co-located inline panel on the Plan
       tab rather than a top-level tab, so the URL `tab=` param
       reflects it too (`tab=compare` while Compare is inline-toggled
       on Plan). Keeps existing share-links + bookmarks working. */
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        if (compareOpen && tab === "plan") p.set("tab", "compare");
        else if (tab === "submission") p.delete("tab");
        else p.set("tab", tab);
        const next = p.toString();
        const url = window.location.pathname + (next ? `?${next}` : "");
        window.history.replaceState(null, "", url);
    }, [tab, compareOpen]);

    const isDoctorPlanned = c.planning_mode === "doctor";
    // Appointments removed from the case page in 2026-02-27 — they're patient-
    // level data, accessible directly from the patient profile.
    const TABS = [
        // iter83/84 — Overview + Shipments split. Overview is the doctor's
        // ACTION hub (banner, fitment, advance, refinement, retainer,
        // confirm-receipt). Shipments is the read-only REFERENCE
        // surface (full shipment timeline, fabrication specs, materials
        // traceability, concerns history, raise-concern). Strict
        // separation makes each tab's purpose obvious.
        ["approved", "in_production", "shipped", "delivered", "aligners_received",
         "in_treatment", "treatment_started", "refinement_requested", "completed"].includes(c.status)
            && { id: "overview",   label: "Treatment",         Icon: Tooth,    hint: "What to do next: receipt, fitting, advance, refinement, retainers" },
        { id: "submission",        label: "Submission",        Icon: ClipboardText,  hint: "What you sent to your planning team" },
        { id: "plan",              label: "Treatment Plan",    Icon: NotePencil,     hint: isDoctorPlanned ? "Author your own plan" : "Plan from your planning team" },
        /* iter98.99bl — permanent History tab next to Treatment Plan.
           Shows every iteration of the doctor↔planner round-trip
           (plan v1 sent → revision req → planner response → plan v2 …)
           so the audit trail is always one click away. */
        (c.revision_requests || []).length > 0 || ((c.plan_versions || []).length > 1)
            ? { id: "history",     label: "History",           Icon: ClockClockwise, hint: "Revision rounds and planner responses for this case" }
            : null,
        ["approved", "in_production", "shipped", "delivered", "aligners_received",
         "in_treatment", "treatment_started", "refinement_requested", "completed"].includes(c.status)
            && { id: "shipments",  label: "Shipments",         Icon: Factory,  hint: "Fabrication specs, dispatch & receipt timeline, materials, and concerns history" },
        { id: "notes",             label: "Notes",             Icon: NotePencil,     hint: "Your private clinical journal for this case" },
        { id: "chat",              label: "Lab Chat",          Icon: ChatCircleDots, hint: "Chat with your Invisique™ planning team about this case" },
        { id: "timeline",          label: "Timeline",          Icon: ClockClockwise, hint: "Every action on this case" },
    ].filter(Boolean);

    const needsApproval = ["sent_for_approval", "ready_for_review"].includes(c.status) && canApprove;
    // iter98.99bl — When the planner has actioned a doctor revision,
    // the doctor reviews the response right at the top of the
    // Treatment Plan tab (PlannerResponseHero) — no separate page.
    const hasActionedRevision = needsApproval &&
        (c.revision_requests || []).some((r) => r.status === "actioned");
    const jumpToPlannerResponse = () => {
        setTab("plan");
        // Wait for tab swap, then scroll to the hero.
        setTimeout(() => {
            document.querySelector('[data-testid="planner-response-hero"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
    };

    return (
        <div data-testid="doctor-case-tabs">
            {/* Sticky "Awaiting your review" banner — only shows when the
                doctor must act. There are two variants:
                  - Initial review (no revisions yet): violet banner →
                    jumps to inline Plan tab where the legacy footer
                    lives (existing behaviour).
                  - Post-resubmit (planner actioned a revision): teal
                    banner → opens the full-page Review & Approve screen
                    showing a side-by-side diff of what changed.
                The banner stays in viewport while the doctor scrolls
                through tabs so they can't lose track. */}
            {hasActionedRevision && tab !== "plan" && (
                <div
                    className="sticky top-0 z-30 -mt-2 mb-4 bg-[#E1F5EE] border border-[#1D9E75] rounded-xl px-4 py-3 flex items-center justify-between gap-3 shadow-sm"
                    data-testid="case-resubmit-sticky-bar"
                >
                    <div className="flex items-start gap-2 min-w-0">
                        <ArrowsClockwise size={16} weight="bold" className="text-[#1D9E75] shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <div className="font-bold text-sm text-[#085041]">Planner has resubmitted with your revisions actioned</div>
                            <p className="text-xs text-[#085041]/80 mt-0.5">
                                Jump to the Treatment Plan tab — you&apos;ll see exactly what the planner did with each item, then approve or request more changes.
                            </p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        onClick={jumpToPlannerResponse}
                        className="bg-[#1D9E75] hover:bg-[#178561] text-white rounded-full shrink-0 font-semibold"
                        data-testid="case-review-approval-btn"
                    >
                        See response
                        <ArrowRight size={12} className="ml-1" weight="bold" />
                    </Button>
                </div>
            )}
            {needsApproval && !hasActionedRevision && tab !== "plan" && (
                <div
                    className="sticky top-0 z-30 -mt-2 mb-4 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 shadow-sm"
                    data-testid="case-review-sticky-bar"
                >
                    <div className="flex items-start gap-2 min-w-0">
                        <Sparkle size={16} weight="fill" className="text-violet-600 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <div className="font-bold text-sm text-violet-900">Plan is ready for your review</div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Your Invisique™ planning team has delivered the treatment setup. Approve to send into production, or request a revision with notes.
                            </p>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        onClick={() => setTab("plan")}
                        className="bg-violet-600 hover:bg-violet-700 text-white rounded-full shrink-0"
                        data-testid="case-review-jump-btn"
                    >
                        Open plan
                        <ArrowRight size={12} className="ml-1" weight="bold" />
                    </Button>
                </div>
            )}

            <DoctorWorkspaceDeck
                tabs={TABS.map((t) => ({
                    ...t,
                    /* Rose pulse dot on the Plan tab when the doctor
                       has an approval waiting. Matches the planner
                       deck's attention affordances. */
                    pulseDot: t.id === "plan" && needsApproval,
                }))}
                activeTab={tab}
                onTabChange={setTab}
                testIdPrefix="doctor-case-tab"
                listTestId="doctor-case-tablist"
            />

            <div className="pt-5 space-y-5">
                {tab === "submission" && (
                    <CaseBriefCard
                        c={c}
                        doctorName={user?.full_name || user?.name}
                        onChange={onChange}
                    />
                )}

                {/* iter83/84 — Overview tab: pure action hub. Status-aware
                    banner + status-specific action cards. Reference data
                    moved to the Shipments tab. */}
                {tab === "overview" && <WorkflowTab caseData={c} onReload={onChange} />}

                {/* iter83/84 — Shipments tab: pure reference surface.
                    Full shipment timeline, fabrication spec history,
                    materials traceability, concerns history per
                    fabrication card. "Raise a concern" stays here
                    because it requires the per-fabrication context
                    that lives on this tab. */}
                {tab === "shipments" && <LabProductionTab caseId={c.id} />}

                {tab === "plan" && (
                    isDoctorPlanned ? (
                        <DoctorSelfPlanView
                            c={c}
                            onChange={onChange}
                            canSubmit={canApprove}
                            doctorName={user?.full_name || user?.name}
                        />
                    ) : (
                        <>
                            {/* iter79 — Compare iterations is now inline on the
                                Plan tab (used to be its own top-level tab). We
                                only show the toggle when there's actually
                                something to compare — keeps the Plan header
                                clean for fresh cases with a single plan.
                                iter98.99dd — Hardened the gate: hide the chip
                                entirely until at least 2 iteration snapshots
                                exist. Single-plan cases (the demo's typical
                                state) now read clean. */}
                            {(c.plan_versions || []).length >= 2 && (
                                <div className="flex items-center justify-end mb-3">
                                    <button
                                        type="button"
                                        onClick={() => setCompareOpen((v) => !v)}
                                        className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold border transition-colors ${
                                            compareOpen
                                                ? "bg-primary text-white border-primary"
                                                : "bg-card text-foreground/80 border-border hover:bg-secondary/40"
                                        }`}
                                        data-testid="plan-compare-toggle"
                                        title="Side-by-side diff across plan iterations"
                                    >
                                        <GitDiff size={12} weight={compareOpen ? "fill" : "regular"} />
                                        {compareOpen ? "Hide comparison" : "Compare iterations"}
                                    </button>
                                </div>
                            )}
                            {compareOpen ? (
                                <PlanCompareTab caseId={c.id} />
                            ) : (
                                /* iter97.8 — Per-tooth chart, attachments, and
                                   wear schedule now render INSIDE the iteration
                                   card body (PlanIterationsStack) so they're
                                   visually part of the "Treatment plan" container
                                   instead of orphan cards floating above it.

                                   iter98.fix-tier-ownership — for CoE cases in
                                   approval status, the PlanApprovalHero surfaces
                                   the planner-decided tier + cost + ETA + plan
                                   highlights above the iterations stack so the
                                   doctor sees a single commit-card before
                                   scrolling into the per-iteration detail. */
                                <>
                                    {/* iter98.99bl — Show the planner's
                                        per-item response above the plan
                                        whenever there's an actioned
                                        revision request in history. */}
                                    <PlannerResponseHero
                                        c={c}
                                        onJumpToHistory={() => setTab("history")}
                                    />
                                    <PlanApprovalHero c={c} onChange={onChange} setupFiles={setupFiles} />
                                    <PlanIterationsStack
                                        c={c}
                                        setupFiles={setupFiles}
                                        canApprove={canApprove}
                                        onApprove={onApprove}
                                        onChange={onChange}
                                    />
                                </>
                            )}
                        </>
                    )
                )}

                {tab === "chat" && <CaseChatThread caseId={c.id} role="doctor" />}

                {/* iter98.99bl — History tab: chronological round-trip
                    feed (planner sends v1 → doctor revision → planner
                    response → planner sends v2 …). */}
                {tab === "history" && <CaseHistoryTab c={c} />}

                {tab === "notes" && (
                    <DoctorCaseNotes c={c} user={user} />
                )}

                {tab === "timeline" && (
                    <CaseTimelineTab timeline={c.timeline || []} viewerRole="doctor" />
                )}
            </div>
        </div>
    );
}


/* ─── DoctorCaseNotes — per-doctor private workspace on a case ─── */
function DoctorCaseNotes({ c, user }) {
    /* Reuses the patient profile's Clinical Notes UI — same look, same
       editor, same data source — but scoped to this case. Note rows from
       other cases / patient-wide notes are filtered out. The composer
       pre-tags new notes to this case. */
    return (
        <PatientNotesSection
            patientId={c.patient_id}
            patientName={c.patient_name}
            cases={[{ id: c.id, code: c.code || c.case_code }]}
            currentUser={user}
            caseFilter={c.id}
        />
    );
}

async function apiPutDoctorNotes(caseId, notes) {
    const r = await api.put(`/cases/${caseId}/doctor-notes`, { notes });
    return r.data;
}

/* ─────────── Case brief — what the doctor submitted ───────────
   Frame as a "memo from the doctor to the COE". Mirrors the fields
   captured by CaseSubmissionWizard so the doctor can sanity-check what
   the planner is working from. Pairs visually with `PlanFromCOE` below
   — together they read as a two-sided conversation. */
const CASE_TYPE_LABEL = {
    full: "Full",
    lite: "Lite",
    single_arch: "Single arch",
    express: "Express",
};
/* iter98.99da — User direction (2026-02-20):
   The "Case type" header slot was showing clinical labels (FULL / TEEN
   / LITE) that never resonated with the doctor — they expected this
   slot to surface the planning choice they made at submission
   (CoE-Planned vs Self-Planned). PLAN_MODE_LABEL is the new mapping
   surfaced in CaseBriefCard's headline + collapsed summary. */
const PLAN_MODE_LABEL = {
    coe: "CoE-Planned",
    doctor: "Self-Planned",
};
const ARCH_LABEL = { upper: "Upper", lower: "Lower", both: "Both" };
const EXTRACTION_LABEL = {
    none: "No extractions",
    planned: "Extractions planned",
    already_extracted: "Already extracted",
};
const PREF_LABEL = {
    avoid: "Avoid", minimal: "Minimal", as_needed: "As needed", aggressive: "Aggressive",
    minimise: "Minimise", standard: "Standard",
    class_ii: "Class II", class_iii: "Class III",
};
const OBJECTIVE_LABEL = {
    alignment: "Alignment", bite_correction: "Bite correction",
    midline_correction: "Midline correction", crowding: "Crowding",
    spacing: "Spacing", deep_bite: "Deep bite", open_bite: "Open bite",
    crossbite: "Crossbite",
};

function humanise(map, v) { return v ? (map[v] || v.replace(/_/g, " ")) : null; }

function fmtINR(n) {
    if (n == null || Number.isNaN(Number(n))) return null;
    return `₹${Number(n).toLocaleString("en-IN")}`;
}

/* ──────────────────────────────────────────────────────────────────────
   CancelledCaseOverlay
   ────────────────────
   iter100.78 — Terminal-state guard for cancelled cases. Replaces the
   entire clinical surface (pathway, submission, treatment plan, notes,
   lab chat, timeline) with a single full-page card so the doctor sees
   ONLY the cancellation context. Includes:
     • Cancellation reason (verbatim, from `c.cancellation_reason`)
     • When it was cancelled (`c.cancelled_at`)
     • Who cancelled it (`c.cancelled_by_name`)
     • Clear "Back to Aligner Cases" CTA
─────────────────────────────────────────────────────────────────────── */
function CancelledCaseOverlay({ c }) {
    const cancelledAt = c.cancelled_at ? new Date(c.cancelled_at) : null;
    const cancelledAtDisplay = (() => {
        if (!cancelledAt) return null;
        try {
            return cancelledAt.toLocaleString("en-IN", {
                day: "numeric", month: "short", year: "numeric",
                hour: "numeric", minute: "2-digit", hour12: true,
            });
        } catch { return cancelledAt.toISOString(); }
    })();
    const reason = (c.cancellation_reason || "").trim();
    const cancelledBy = (c.cancelled_by_name || "").trim();
    return (
        <div
            className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50/70 via-white to-slate-50 shadow-sm overflow-hidden"
            data-testid="doctor-case-cancelled-overlay"
        >
            <div className="px-6 py-5 sm:px-8 sm:py-6 flex flex-col sm:flex-row items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-rose-100 text-rose-700 grid place-items-center shrink-0 shadow-inner">
                    <Lock size={28} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-rose-700/80">
                        Case cancelled · terminated
                    </div>
                    <div className="mt-1.5 font-display text-xl sm:text-2xl font-bold text-foreground leading-tight">
                        This case is closed. No further action can be taken.
                    </div>
                    <div className="mt-2 text-sm text-foreground/70 leading-relaxed max-w-2xl">
                        The clinical pathway, treatment plan, submission, notes,
                        and lab chat for this case are no longer available. If
                        you need to restart aligner treatment for this patient,
                        create a new case from the patient&apos;s profile.
                    </div>

                    {/* Cancellation details */}
                    <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                        {reason && (
                            <div
                                className="sm:col-span-2 bg-white border border-rose-200 rounded-xl px-4 py-3"
                                data-testid="cancelled-reason-block"
                            >
                                <dt className="text-[10px] font-bold uppercase tracking-wider text-rose-700/80">
                                    Reason
                                </dt>
                                <dd className="mt-1 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
                                    {reason}
                                </dd>
                            </div>
                        )}
                        {cancelledAtDisplay && (
                            <div className="bg-white border border-rose-100 rounded-xl px-4 py-3">
                                <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Cancelled on
                                </dt>
                                <dd className="mt-1 text-sm text-foreground/90" data-testid="cancelled-at-value">
                                    {cancelledAtDisplay}
                                </dd>
                            </div>
                        )}
                        {cancelledBy && (
                            <div className="bg-white border border-rose-100 rounded-xl px-4 py-3">
                                <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Cancelled by
                                </dt>
                                <dd className="mt-1 text-sm text-foreground/90" data-testid="cancelled-by-value">
                                    {cancelledBy}
                                </dd>
                            </div>
                        )}
                    </dl>

                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        <Link
                            to="/doctor/cases"
                            className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold px-4 py-2 transition-colors"
                            data-testid="cancelled-back-to-cases"
                        >
                            <CaretLeft size={13} weight="bold" /> Back to Aligner Cases
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────
   CaseLockedBanner
   ────────────────
   Surfaces at the top of the case page once the case reaches a terminal
   state (completed / cancelled). The clinical contract is frozen — the
   doctor can't edit the prescription, plan, pricing, or aligner stage.
   But Lab Chat / case notes / progress photos / refinement-or-retainer
   spawn all stay open. The banner makes that boundary explicit so the
   doctor doesn't try to edit and hit an opaque 423 error.
─────────────────────────────────────────────────────────────────────── */
function CaseLockedBanner({ reason, status, onOpenChat }) {
    const isCancelled = status === "cancelled";
    const title = isCancelled ? "Case cancelled" : "Treatment complete";
    return (
        <div
            className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50/40 p-5 flex flex-col sm:flex-row items-start gap-4 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid="doctor-case-locked-banner"
        >
            <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 grid place-items-center shrink-0">
                <Lock size={18} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-600">
                    Clinical record locked
                </div>
                <div className="mt-1 font-display text-base font-bold text-foreground">
                    {title} — case is now read-only.
                </div>
                <div className="mt-1.5 text-sm text-foreground/80 leading-relaxed">
                    {reason || "The plan and prescription are frozen as the medical record. You can still add case notes, upload follow-up progress photos, chat with our team in Lab Chat, and order a refinement or new retainer when needed."}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                        <CheckCircle size={11} weight="fill" /> Notes
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                        <CheckCircle size={11} weight="fill" /> Photos
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                        <CheckCircle size={11} weight="fill" /> Lab Chat
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">
                        <CheckCircle size={11} weight="fill" /> Refinement / Retainer order
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-semibold">
                        <Lock size={11} weight="fill" /> Plan & prescription
                    </span>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                    Need to correct something? Message us in Lab Chat — admin can lift the lock for genuine corrections.
                </div>
                <div className="mt-3">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onOpenChat}
                        className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
                        data-testid="case-locked-open-chat"
                    >
                        <ChatCircleDots size={13} weight="duotone" className="mr-1.5" />
                        Open Lab Chat
                    </Button>
                </div>
            </div>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────
   InfoRequestBanner
   ─────────────────
   Surfaced at the top of the case page while a case is paused on
   `info_requested`. The planner's exact question lives here verbatim so
   the doctor can read it without digging into the Timeline tab — same
   text the notification bell preview shows. Two primary actions:
     - Open the Lab Chat to converse first if a back-and-forth is needed
     - Resubmit after info — opens a Dialog (iter97.46) that lets the
       doctor type a descriptive reply (e.g. "Re-scanned upper arch, new
       STL uploaded; chief complaint updated to crowding + crossbite").
       The reply is persisted to `last_info_response_note` and surfaces
       on the planner's "Doctor responded" banner alongside their
       original question — so the planner sees Q + A side-by-side
       without having to dig into Provider Chat.
   Designed in amber to signal "pause" without alarming red.
─────────────────────────────────────────────────────────────────────── */
function InfoRequestBanner({ note, onResubmit, onOpenChat }) {
    const [open, setOpen] = useState(false);
    const [reply, setReply] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await onResubmit(reply.trim());
            setOpen(false);
            setReply("");
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            <div
                className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex flex-col sm:flex-row items-start gap-4"
                data-testid="doctor-info-request-banner"
            >
                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-800 grid place-items-center shrink-0">
                    <ChatCircleDots size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-amber-900/80">
                        Your planning team needs more info
                    </div>
                    <div className="mt-1.5 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap" data-testid="doctor-info-request-note">
                        {note}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                            size="sm"
                            onClick={() => setOpen(true)}
                            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="doctor-info-request-resubmit"
                        >
                            <CheckCircle size={13} weight="bold" className="mr-1.5" />
                            Reply &amp; resubmit
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={onOpenChat}
                            className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-100"
                            data-testid="doctor-info-request-chat"
                        >
                            <ChatCircleDots size={13} weight="duotone" className="mr-1.5" />
                            Reply in Lab Chat
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="doctor-info-response-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-lg flex items-center gap-2">
                            <CheckCircle size={18} weight="duotone" className="text-amber-600" />
                            Reply &amp; resubmit to planner
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            Your planner asked: <span className="italic text-foreground/90">&ldquo;{note}&rdquo;</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold">What did you address? <span className="text-muted-foreground font-normal">(optional but recommended)</span></Label>
                            <Textarea
                                rows={5}
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                placeholder="e.g. Re-scanned the upper arch and uploaded the new STL. Chief complaint updated to include the crossbite. Let me know if anything else is needed."
                                className="resize-none text-sm mt-1.5"
                                data-testid="doctor-info-response-note"
                                disabled={submitting}
                                maxLength={2000}
                            />
                            <div className="mt-1 text-[10px] text-muted-foreground text-right tabular-nums">
                                {reply.length} / 2000
                            </div>
                        </div>
                        <div className="text-[11px] text-muted-foreground bg-secondary/40 border border-border rounded-lg px-2.5 py-2 leading-relaxed">
                            Your reply goes directly on this case so the planner sees your question + answer in one place when they reopen it. For longer back-and-forth, use <span className="font-semibold">Lab Chat</span> instead.
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={submitting}
                            className="rounded-full"
                            data-testid="doctor-info-response-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="doctor-info-response-submit"
                        >
                            <CheckCircle size={13} weight="bold" className="mr-1.5" />
                            {submitting ? "Sending…" : "Send back to planner"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/* Section helper — module-scope tinted card with uppercase tracking-[0.2em]
   label + optional Phosphor icon. Used by CaseBriefCard to render every
   block (Diagnosis, Objectives, Clinical, Rx prefs, Notes) with the same
   premium rhythm we use across our PDFs.

   iter98.99de — Lifted into a shared SectionCard module so the same
   rhythm can be applied across the Treatment Plan tab components
   (PlanIterationsStack, PlannerResponseHero, DoctorSelfPlanView) —
   keeping the case detail page reading as one consistent piece. The
   local `Section` name is preserved as an alias so the existing call
   sites in CaseBriefCard render identically. */
const Section = SectionCard;


function CaseBriefCard({ c, doctorName, onChange }) {
    const cl = c.clinical || {};
    const rx = c.prescription || {};
    const pricing = c.pricing || {};
    const planningMode = c.planning_mode || "coe";

    /* iter98.99da — Headline now leads with the doctor's PLAN-TYPE
       choice (CoE-Planned / Self-Planned). Clinical case_type
       (full/teen/lite) was confusing in this slot — that detail still
       lives on Admin/Planner surfaces where it actually drives workflow. */
    const headlineFacts = [
        // iter98.99ae — Dropped redundant "Plan type" (also in service strip
        // below). The headline now reads as a clean clinical snapshot.
        { label: "Malocclusion", value: cl.malocclusion_class ? `Class ${cl.malocclusion_class}` : null },
        { label: "Arch", value: humanise(ARCH_LABEL, cl.arch) },
        { label: "Extraction", value: humanise(EXTRACTION_LABEL, cl.extraction_plan) },
    ];

    /* iter98.99da — Service selections moved INTO the brief per user
       direction: "why do we need another container?". Mirrors the rows
       SubmissionSelectionsCard used to show, now folded directly below
       the headline. Fee remains on Treatment Plan tab (commercial). */
    const priorityLabel = pricing.priority_label || "Standard TAT";
    const priorityTat = pricing.priority_tat_days ? `${pricing.priority_tat_days}-day TAT` : null;
    const priorityDisplay = priorityTat ? `${priorityLabel} · ${priorityTat}` : priorityLabel;
    const scannerRequested = !!pricing.scanner_pickup_requested;
    /* iter98.99af (2026-06) — Surface the Compliance Monitoring & Support
       opt-in alongside the other service selections so the doctor's
       Yes/No choice is visible at a glance. Boolean (with `null` meaning
       "not yet chosen", which only happens on legacy drafts). */
    const complianceChosen = typeof c.coe_support_compliance === "boolean";
    const complianceOptedIn = c.coe_support_compliance === true;
    const serviceFacts = [
        { label: "Planning service", value: humanise(PLAN_MODE_LABEL, planningMode), full: true },
        { label: "Manufacturing speed", value: priorityDisplay },
        { label: "IO Scans by Invisique™", value: scannerRequested ? "Opted in" : "Not requested", muted: !scannerRequested },
        {
            label: "Compliance Monitoring",
            value: complianceChosen ? (complianceOptedIn ? "Opted in" : "Declined") : "Not chosen",
            muted: !complianceOptedIn,
        },
    ];
    const objectives = Array.isArray(rx.objectives) ? rx.objectives : [];

    // Doctor-uploaded records (everything except COE deliverables)
    const COE_KINDS = new Set(["setup", "setup_plan", "plan_video"]);
    const doctorFiles = (c.files || []).filter((f) => !COE_KINDS.has(f.kind));

    /* iter98.99ae — The Submission tab is read-only once the doctor has
       sent the case. We expose a Locked pill on the header for clarity. */
    const isLocked = !["draft", "info_requested", "revision_requested"].includes(c.status);

    return (
        <div
            className="bg-card rounded-2xl border border-border overflow-hidden relative shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid="case-brief-card"
        >
            {/* Attribution header — looks like the top of a memo */}
            <div
                className="w-full px-5 sm:px-6 pt-5 pb-4 bg-primary/[0.04] flex items-center justify-between gap-3 border-b border-border"
                data-testid="brief-header"
            >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-full bg-primary text-white grid place-items-center font-semibold text-xs shrink-0">
                        {initials(doctorName) || "DR"}
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary/80">From you · Case brief</div>
                        <div className="text-sm font-semibold truncate">
                            {doctorName || "Doctor"}
                            <span className="text-muted-foreground font-normal"> · </span>
                            <span className="text-muted-foreground font-normal text-xs">to your planning team</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="text-[11px] text-muted-foreground tabular-nums hidden md:block">
                        Submitted {fmtDate(c.created_at)}
                    </div>
                    {isLocked && (
                        <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary border border-border text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground"
                            data-testid="brief-locked-pill"
                            title="This submission is locked. The planning team is now working on it."
                        >
                            <Lock size={10} weight="duotone" /> Locked
                        </span>
                    )}
                    <ViewSubmissionLink caseId={c.id} />
                </div>
            </div>

            {/* Single, always-expanded body — brief content */}
            <div className="px-5 sm:px-6 py-5 space-y-4">
                {/* Services strip — wrapped in Section card so it
                    matches the rhythm of every other block. */}
                <Section icon={Factory} label="Service selections" testid="brief-services">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="brief-services-strip">
                        {serviceFacts.map((f) => (
                            <BriefFact
                                key={f.label}
                                label={f.label}
                                value={f.value}
                                muted={f.muted}
                                tone="emerald"
                            />
                        ))}
                    </div>
                </Section>

                {/* Diagnosis — wrapped in Section card for visual consistency */}
                {c.diagnosis && (
                    <Section icon={ClipboardText} label="Diagnosis" testid="brief-diagnosis">
                        <p className="font-display text-[15px] leading-snug text-foreground/90">{c.diagnosis}</p>
                    </Section>
                )}

                {/* Treatment objectives — prominent pill row inside its own
                    Section card so the rhythm of the page is consistent. */}
                {objectives.length > 0 && (
                    <Section icon={Sparkle} label="Treatment objectives" testid="brief-objectives">
                        <div className="flex flex-wrap gap-2">
                            {objectives.map((o) => (
                                <span
                                    key={o}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold bg-primary/12 text-primary border border-primary/25 ring-1 ring-primary/5 shadow-sm"
                                    data-testid={`brief-objective-${o}`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                    {humanise(OBJECTIVE_LABEL, o)}
                                </span>
                            ))}
                        </div>
                    </Section>
                )}

                {/* Clinical case — headline facts + supplementary detail in
                    one consolidated section. Removes the previous layout
                    where extraction-teeth lived in a separate grid below
                    the headline strip — now everything clinical is in one
                    visually unified card. */}
                <Section icon={Tooth} label="Clinical case" testid="brief-clinical-section">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                        {headlineFacts.filter((f) => f.value).map((f) => (
                            <BriefFact key={f.label} label={f.label} value={f.value} />
                        ))}
                    </div>
                    {(cl.chief_complaint || cl.special_notes || cl.extraction_teeth || cl.perio_status) && (
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-2 border-t border-border/50">
                            {cl.extraction_teeth && <BriefRow label="Extraction teeth" value={cl.extraction_teeth} compact />}
                            {cl.perio_status && <BriefRow label="Perio status" value={cl.perio_status} compact />}
                            {cl.chief_complaint && <BriefRow label="Chief complaint" value={cl.chief_complaint} compact multiline />}
                            {cl.special_notes && <BriefRow label="Special notes" value={cl.special_notes} compact multiline />}
                        </div>
                    )}
                </Section>

                {/* Prescription preferences — only render when populated */}
                {(rx.ipr_preference || rx.attachments_preference || rx.elastics_preference || rx.do_not_touch_teeth || rx.esthetic_goals) && (
                    <Section icon={Prescription} label="Prescription preferences" testid="brief-rx-section">
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                            {rx.ipr_preference && <BriefRow label="IPR" value={humanise(PREF_LABEL, rx.ipr_preference)} compact />}
                            {rx.attachments_preference && <BriefRow label="Attachments" value={humanise(PREF_LABEL, rx.attachments_preference)} compact />}
                            {rx.elastics_preference && <BriefRow label="Elastics" value={humanise(PREF_LABEL, rx.elastics_preference)} compact />}
                            {rx.do_not_touch_teeth && <BriefRow label="Do-not-touch teeth" value={rx.do_not_touch_teeth} compact />}
                            {rx.esthetic_goals && <BriefRow label="Esthetic goals" value={rx.esthetic_goals} compact multiline />}
                        </div>
                    </Section>
                )}

                {/* Treatment notes — free-form, gets its own section so the
                    doctor's voice doesn't get lost in the clinical rows. */}
                {c.treatment_notes && (
                    <Section icon={NotePencil} label="Notes to planner" testid="brief-notes-section">
                        <p className="text-[13px] leading-relaxed text-foreground/85 whitespace-pre-wrap">{c.treatment_notes}</p>
                    </Section>
                )}

                {/* Records previously lived here as a read-only preview. They
                    now live below the brief as a fully managed
                    `CaseRecordsManager` so doctors can replace or remove
                    incorrect files while the case is still editable. */}

                {/* iter98.99bd — Removed the "Treatment tier & pricing"
                    panel from the Submission tab per user direction
                    (2026-02-19). The Submission tab should surface ONLY
                    the doctor's case brief; tier + pricing live on the
                    Treatment Plan tab's Payment & Offers panel where the
                    doctor commits to a tier as part of approval. */}
            </div>

            {/* iter98.99ad — Records folded into the same container as the
                Case Brief per user direction. Renders inline (no outer
                chrome) so the whole Submission tab reads as ONE document. */}
            <CaseRecordsManager
                caseId={c.id}
                caseStatus={c.status}
                files={c.files || []}
                onChange={onChange}
                inline
            />
        </div>
    );
}


/* iter98.99ad — "View Submission" link in the brief header. Opens the
   backend-rendered PDF in a new tab so the doctor can show the patient,
   print, or download the exact same artefact the planner sees. */
function ViewSubmissionLink({ caseId }) {
    const href = `${API}/cases/${caseId}/pdf/submission?auth=${encodeURIComponent(localStorage.getItem("ca_token") || "")}`;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border text-[11px] font-semibold text-primary hover:bg-primary/5 hover:border-primary/30 transition-colors shrink-0"
            data-testid="brief-view-submission"
            title="Open the submission as a printable PDF"
        >
            <FilePdf size={12} weight="duotone" />
            View Submission
        </a>
    );
}

function BriefFact({ label, value, muted, tone }) {
    /* iter98.99da — `tone="emerald"` paints the service-selection cards
       in a subtle emerald wash so they read as a distinct sub-group
       inside the brief without breaking the single-container rhythm. */
    const containerClass = tone === "emerald"
        ? "rounded-xl bg-emerald-50/50 border border-emerald-200/70 px-3 py-2.5"
        : "rounded-xl bg-white border border-border/60 px-3 py-2.5";
    const labelClass = tone === "emerald"
        ? "text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-800/80 mb-0.5"
        : "text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-0.5";
    const valueClass = !value
        ? "text-sm font-semibold text-muted-foreground/60 italic"
        : muted
        ? "text-sm font-semibold text-muted-foreground/70 italic"
        : "text-sm font-semibold";
    return (
        <div className={containerClass}>
            <div className={labelClass}>{label}</div>
            <div className={valueClass}>
                {value || "—"}
            </div>
        </div>
    );
}

function BriefLabel({ children }) {
    return (
        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">{children}</div>
    );
}

function BriefRow({ label, value, multiline, compact }) {
    return (
        <div>
            <div className={`text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground ${compact ? "mb-0.5" : "mb-1"}`}>{label}</div>
            <div className={`text-sm ${multiline ? "whitespace-pre-line" : ""}`}>{value}</div>
        </div>
    );
}

/* ─────────── Plan from COE — visual mirror of the Case Brief ───────────
   Wraps the existing PlanPanel in a violet-tinted "delivered package"
   chrome so the Plan visually contrasts with the Case Brief above. The
   page now reads as a two-sided conversation: doctor's memo → COE's
   delivered plan. Adds a per-case collapse toggle (persisted in
   localStorage) and a Compare-with-previous CTA when 2+ snapshots exist. */
/* ─────────── Plan from YOU — bring-your-own-plan workflow ───────────
   Mirror-image of `PlanFromCOE`. Visual chrome uses teal (the doctor's
   primary identity colour, matching the Case Brief stripe) instead of
   violet, because here the *doctor* is the author. Wraps `PlanEditorInline`
   with `savePath` pointing at the new `/cases/{id}/doctor-plan` endpoint
   so the same auto-save UX works without code duplication. Submit-to-lab
   action lives in the footer. */
/* ─────────────────────────────────────────────────────────────────────
   Doctor Self-Plan Author shell (iter97.37 — Phase A)

   The full implementation lives in /components/case/DoctorSelfPlanEditor.jsx
   so this page stays focused on tab-routing + COE-plan review. The
   wrapper handles the 4-tab UI, sticky bottom bar, blockers, summary,
   submit, and post-submit confirmation.
   ───────────────────────────────────────────────────────────────────── */

function PlanFromCOE({ c, setupFiles, children, actionFooter, hasPendingAction }) {
    const planDeliveredAt = c.plan?.updated_at || c.plan?.created_at;
    const planVersion = c.plan?.version;
    const hasAnyPlan = !!(c.plan?.external_link || c.plan?.video_file_id
        || c.plan?.aligner_count_upper || c.plan?.proposed_attachments
        || c.plan?.planner_notes || setupFiles.length > 0);

    const storageKey = `planFromCoeCollapsed:${c.id}:v2`;
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const v = localStorage.getItem(storageKey);
            return v === null ? true : v === "1";
        } catch { return true; }
    });
    useEffect(() => {
        try { localStorage.setItem(storageKey, collapsed ? "1" : "0"); }
        catch (err) { console.debug("plan collapse persist skipped:", err); }
    }, [collapsed, storageKey]);

    /* Version-compare: fetch snapshots once when the dialog opens. Two or
       more versions = an "Compare with previous" CTA appears in the
       header. Diff is shown in a Dialog so collapse/scroll state of the
       page is preserved underneath. */
    const [versions, setVersions] = useState(null);
    const [compareOpen, setCompareOpen] = useState(false);
    useEffect(() => {
        let mounted = true;
        api.get(`/cases/${c.id}/plan-versions`)
            .then((r) => { if (mounted) setVersions(r.data || []); })
            .catch((err) => { console.debug("plan-versions fetch skipped:", err); });
        return () => { mounted = false; };
    }, [c.id]);
    const canCompare = (versions?.length || 0) >= 2;

    const collapsedSummary = [
        c.plan?.external_link && "Link",
        c.plan?.video_file_id && "Video",
        c.plan?.aligner_count_upper && `${c.plan.aligner_count_upper} U / ${c.plan?.aligner_count_lower || "—"} L`,
        c.plan?.estimated_duration_weeks && `${c.plan.estimated_duration_weeks} wk`,
    ].filter(Boolean);

    return (
        <div
            className="rounded-2xl border border-cyan-200 overflow-hidden relative bg-gradient-to-br from-cyan-50/60 via-white to-white shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid="plan-from-coe"
        >
            {/* Attribution header — clickable to collapse/expand */}
            <div className={`px-5 sm:px-6 pt-5 pb-3 bg-cyan-50/60 ${collapsed ? "" : "border-b border-cyan-200/70"}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        className="flex items-center gap-3 min-w-0 flex-1 text-left -m-1 p-1 rounded-md hover:bg-cyan-100/60"
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? "Expand treatment plan" : "Minimise treatment plan"}
                        data-testid="plan-collapse-toggle"
                    >
                        <div className="w-9 h-9 rounded-full bg-violet-600 text-white grid place-items-center shrink-0">
                            <Sparkle size={16} weight="fill" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-violet-700">From Invisique™ COE · Treatment plan</div>
                            <div className="text-sm font-semibold truncate flex items-center gap-2 flex-wrap">
                                Center of Excellence — Planning
                                {planVersion ? <span className="text-violet-700 font-normal">· v{planVersion}</span> : null}
                                {hasPendingAction && (
                                    <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-200 text-[10px] font-bold uppercase tracking-wide animate-pulse"
                                        data-testid="plan-pending-action-badge"
                                    >
                                        <Sparkle size={10} weight="fill" /> 1 pending action
                                    </span>
                                )}
                            </div>
                            {collapsed && collapsedSummary.length > 0 && (
                                <div className="mt-1.5 text-[10px] text-muted-foreground">
                                    {collapsedSummary.map((s, i) => (
                                        <span key={s}>{i > 0 ? <span className="mx-1.5 opacity-50">·</span> : null}{s}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                        {canCompare && (
                            <Button
                                size="sm" variant="outline"
                                onClick={() => setCompareOpen(true)}
                                className="rounded-full text-violet-700 border-violet-300 hover:bg-violet-50 text-xs h-8"
                                data-testid="plan-compare-btn"
                            >
                                <GitDiff size={13} className="mr-1" /> Compare with previous
                            </Button>
                        )}
                        <div className="text-[11px] text-muted-foreground tabular-nums hidden sm:block">
                            {hasAnyPlan
                                ? <>Delivered {fmtDate(planDeliveredAt || c.updated_at)}</>
                                : <>Planning in progress</>}
                        </div>
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            className="w-7 h-7 rounded-full bg-white border border-violet-200 grid place-items-center text-violet-700 hover:text-violet-900"
                            aria-label={collapsed ? "Expand" : "Minimise"}
                            data-testid="plan-collapse-icon"
                        >
                            {collapsed ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" />}
                        </button>
                    </div>
                </div>
            </div>

            {!collapsed && (
                <div className="px-5 sm:px-6 py-5 space-y-4">
                    {children}
                    {setupFiles.length > 0 && (
                        <div className="rounded-xl bg-white border border-violet-200/70 p-3 space-y-1.5">
                            <BriefLabel>Attached deliverables ({setupFiles.length})</BriefLabel>
                            {setupFiles.map((f) => (
                                <a
                                    key={f.id}
                                    href={`${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-sm text-violet-700 hover:underline flex items-center gap-1.5"
                                    data-testid={`setup-file-${f.id}`}
                                >
                                    <FileArrowUp size={14} /> {f.name}
                                </a>
                            ))}
                        </div>
                    )}
                    {/* In-card action footer — Approve / Request revision /
                        passive "in QA review" status. Lives in the same card
                        so reading the plan and deciding on it never breaks
                        eye-line. */}
                    {actionFooter}
                </div>
            )}

            {canCompare && (
                <PlanVersionCompareDialog
                    open={compareOpen}
                    onClose={() => setCompareOpen(false)}
                    versions={versions}
                />
            )}
        </div>
    );
}

/* ─────────── Plan version compare ───────────
   Shows the latest plan side-by-side with the previous snapshot, with
   changed fields highlighted amber. The plan snapshot model is whatever
   the backend stored — we only diff the human-readable fields. */
const COMPARE_FIELDS = [
    ["external_link", "Plan viewer link"],
    ["video_file_id", "Walkthrough video"],
    ["aligner_count_upper", "Upper aligners"],
    ["aligner_count_lower", "Lower aligners"],
    ["estimated_duration_weeks", "Duration (weeks)"],
    ["change_interval_days", "Change interval (days)"],
    ["initial_overjet_mm", "Initial overjet (mm)"],
    ["initial_overbite_mm", "Initial overbite (mm)"],
    ["final_overjet_mm", "Final overjet (mm)"],
    ["final_overbite_mm", "Final overbite (mm)"],
    ["total_ipr_mm", "Total IPR (mm)"],
    ["proposed_attachments", "Attachments"],
    ["proposed_ipr", "IPR plan"],
    ["proposed_elastics", "Elastics"],
    ["planner_notes", "Planner notes"],
    ["has_overcorrection", "Overcorrection planned"],
    ["overcorrection_notes", "Overcorrection details"],
    ["aux_buttons", "Buttons"],
    ["aux_bite_ramps", "Bite ramps"],
    ["aux_mandibular_advancement", "Mandibular advancement"],
    ["aux_precision_cuts", "Precision cuts"],
    ["aux_hooks", "Hooks"],
    ["aux_other_notes", "Other auxiliaries"],
    ["retainer_type", "Retainer type"],
    ["retention_schedule", "Wear schedule"],
    ["retention_recall_months", "Recall (months)"],
];

function PlanVersionCompareDialog({ open, onClose, versions }) {
    /* Latest = highest version. Previous = the snapshot just before it. */
    const sorted = [...(versions || [])].sort((a, b) => (a.version || 0) - (b.version || 0));
    const latest = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];

    const fmt = (v) => {
        if (v === null || v === undefined || v === "") return "—";
        if (typeof v === "boolean") return v ? "Yes" : "No";
        return String(v);
    };
    const changed = (a, b) => fmt(a) !== fmt(b);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="plan-compare-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <GitDiff size={16} className="text-violet-700" />
                        Plan revision history
                    </DialogTitle>
                    <DialogDescription>
                        Comparing the previous snapshot against the current plan. Changed fields are highlighted.
                    </DialogDescription>
                </DialogHeader>

                {!previous ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">
                        Only one version exists so far — nothing to compare against.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="plan-compare-table">
                            <thead>
                                <tr className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                    <th className="text-left py-2 px-2 w-40">Field</th>
                                    <th className="text-left py-2 px-2">
                                        Previous (v{previous.version})
                                        {previous.created_at && (
                                            <div className="text-[10px] text-muted-foreground/70 font-normal normal-case tracking-normal">
                                                {fmtDate(previous.created_at)}
                                                {previous.reason && <> · {previous.reason.replace(/_/g, " ")}</>}
                                            </div>
                                        )}
                                    </th>
                                    <th className="text-left py-2 px-2">
                                        Current (v{latest.version})
                                        {latest.created_at && (
                                            <div className="text-[10px] text-muted-foreground/70 font-normal normal-case tracking-normal">
                                                {fmtDate(latest.created_at)}
                                                {latest.reason && <> · {latest.reason.replace(/_/g, " ")}</>}
                                            </div>
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {COMPARE_FIELDS.map(([key, label]) => {
                                    const a = (previous.plan || previous)[key];
                                    const b = (latest.plan || latest)[key];
                                    const diff = changed(a, b);
                                    return (
                                        <tr key={key} className={diff ? "bg-amber-50/60" : ""} data-testid={`compare-row-${key}`}>
                                            <td className="py-2 px-2 text-xs text-muted-foreground align-top">{label}</td>
                                            <td className={`py-2 px-2 text-xs align-top ${diff ? "text-rose-700" : "text-foreground/80"}`}>{fmt(a)}</td>
                                            <td className={`py-2 px-2 text-xs align-top ${diff ? "text-emerald-700 font-semibold" : "text-foreground/80"}`}>{fmt(b)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="plan-compare-close">
                        <CloseIcon size={13} className="mr-1.5" /> Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ─────────── Clinical pathway — compact band ───────────
   Uses the brand "letterhead memo" treatment — deep ink surface with a
   violet accent stripe, gold progress fill. Designed to feel like the
   header of a clinical document, not a generic web progress bar. */
function ClinicalPathwayCompact({ currentStatus, mode }) {
    const stages = pathwayStagesFor(mode);
    const idx = pathwayIndex(currentStatus, mode);
    const pct = idx > 0 ? (idx / (stages.length - 1)) * 100 : 0;
    return (
        <div
            className="relative rounded-2xl overflow-hidden border border-border bg-card px-5 py-4"
            data-testid="clinical-pathway"
        >
            {/* iter98.99dg — Strip the right-side textual tag entirely
                per user direction. The status capsule above already
                names the current state; the pathway strip is now a
                purely visual progress indicator (dots + bold label
                under the active dot). Keeps the eye on the capsule
                for "what's the case state" + the strip for "where in
                the journey". */}
            <div className="relative flex items-center mb-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                <span>Clinical pathway{mode === "doctor" ? " · Lab-only" : ""}</span>
            </div>
            <div className="relative h-1.5 bg-secondary rounded-full overflow-hidden">
                <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-400 via-primary to-primary transition-all duration-500"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className={`relative grid gap-0.5 mt-2.5`} style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}>
                {stages.map((s, i) => (
                    <div key={s.id} className="flex flex-col items-center min-w-0">
                        <div
                            className={`w-1.5 h-1.5 rounded-full ${
                                i < idx ? "bg-primary"
                                    : i === idx ? "bg-primary ring-2 ring-primary/25"
                                    : "bg-border"
                            }`}
                        />
                        <span className={`mt-1 text-[9px] truncate w-full text-center leading-tight ${
                            i === idx ? "text-primary font-semibold" : "text-muted-foreground/70"
                        }`}>
                            {s.label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* Lifecycle banner removed in 2026-02-27 — its state-narration was already
   covered by the status pill + the Clinical Pathway band. The single CTA
   per state (submit / resubmit-after-info / confirm-receipt / recall) is
   now surfaced inline on the Patient Header instead. */

/* ─────────── Plan Decision Footer ───────────
   Lives INSIDE the Plan from COE card (passed as `actionFooter`). Status-
   aware: shows "planning in progress" / "in QA review" / "Approve + revise"
   based on case status. The read-attestation gate stays here — it logs to
   the timeline as a medico-legal audit trail. */
function PlanDecisionFooter({ c, canApprove, onApprove, onChange }) {
    const navigate = useNavigate();
    const [planRead, setPlanRead] = useState(false);

    // iter97.52 — the inline revision dialog has been replaced with a
    // dedicated full-page screen. Clicking "Request revision" now
    // navigates to /doctor/cases/:id/request-revision so the doctor can
    // author per-section structured changes (numeric tier/aligner counts,
    // chip-selected retainer type, free-text sub-section revisions) on a
    // proper side-by-side layout against the planner's plan.
    const goToRevision = () => navigate(`/doctor/cases/${c.id}/request-revision`);

    // Passive states: planning / QA — no action, just a status line.
    if (c.status === "in_planning" || c.status === "submitted_to_qa") {
        return (
            <div className="border-t border-violet-200/70 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-4 mt-3 text-sm" data-testid="plan-decision-footer-passive">
                <div className="flex items-center gap-2 text-violet-900/80">
                    <Sparkle size={14} className="text-violet-600" weight="fill" />
                    <span className="font-semibold">
                        {c.status === "submitted_to_qa" ? "Plan is in internal QA review" : "Center of Excellence is preparing your plan"}
                    </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                    You&apos;ll see Approve / Request revision actions here once the plan is delivered.
                </p>
            </div>
        );
    }

    // Decision states: ready_for_review / sent_for_approval
    if (c.status !== "ready_for_review" && c.status !== "sent_for_approval") return null;

    if (!canApprove) {
        return (
            <div className="border-t border-violet-200/70 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-4 mt-3 text-sm text-muted-foreground italic" data-testid="plan-decision-footer-readonly">
                Only the treating doctor can approve or request a revision on this plan.
            </div>
        );
    }

    return (
        <div className="border-t border-violet-200/70 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-4 mt-3 space-y-3" data-testid="plan-decision-footer">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="font-display font-bold text-sm">
                    {(c.planning_mode || "coe") === "doctor" && c.qa_reviewer_name
                        ? "Your decision on this QA-reviewed plan"
                        : "Your decision on this plan"}
                </div>
                <span className="text-[10px] uppercase tracking-wider font-semibold text-violet-700">Plan ready for review</span>
            </div>

            {/* iter98.99g (Phase 2) — Self-Plan QA review banner. When the
                doctor's own plan has just been QA-reviewed and routed back
                for final approval, surface the QA reviewer's note + name
                + timestamp prominently so the doctor knows WHAT was
                reviewed (not just that someone touched it). */}
            {(c.planning_mode || "coe") === "doctor" && c.qa_reviewer_name && (
                <div
                    className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-emerald-50/30 p-3"
                    data-testid="self-plan-qa-review-banner"
                >
                    <div className="flex items-start gap-2.5">
                        <div className="shrink-0 w-8 h-8 rounded-lg bg-emerald-600 text-white grid place-items-center shadow-sm">
                            <CheckCircle size={16} weight="fill" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                                    QA reviewed · ready for your final approval
                                </span>
                            </div>
                            <div className="text-[12px] text-emerald-900 mt-0.5 leading-snug">
                                Reviewed by <span className="font-semibold">{c.qa_reviewer_name}</span>
                                {c.qa_reviewed_at && (
                                    <span className="text-muted-foreground">
                                        {" · "}{new Date(c.qa_reviewed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                )}
                            </div>
                            {c.qa_review_note && c.qa_review_note.trim() && (
                                <div
                                    className="mt-1.5 rounded-md bg-white/70 border border-emerald-200/60 px-2.5 py-1.5 text-[12px] text-foreground/85 leading-snug whitespace-pre-wrap"
                                    data-testid="self-plan-qa-review-note"
                                >
                                    {c.qa_review_note}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* "I have read the plan" attestation — gates the Approve button.
                Medico-legal contract is stamped on the timeline server-side. */}
            <label
                className="flex items-start gap-2 sm:gap-3 cursor-pointer rounded-xl bg-white border border-violet-200/80 p-3"
                data-testid={`approve-attestation-${c.id}`}
            >
                <Checkbox
                    checked={planRead}
                    onCheckedChange={(v) => setPlanRead(v === true)}
                    data-testid={`plan-read-checkbox-${c.id}`}
                    className="mt-0.5"
                />
                <span className="text-xs text-foreground/85 leading-snug">
                    <span className="font-semibold">I have read the plan</span> — I confirm I&apos;ve reviewed the setup, IPR, attachments, auxiliaries, retention and protocol above, and that this plan is clinically appropriate for the patient.
                </span>
            </label>

            <div className="flex flex-wrap gap-2">
                <Button
                    onClick={() => onApprove(true)}
                    disabled={!planRead}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`approve-case-${c.id}`}
                    title={planRead ? undefined : "Tick the read-confirmation above to enable approval"}
                >
                    <CheckCircle size={14} className="mr-1.5" /> Approve plan
                </Button>
                <Button
                    variant="outline"
                    onClick={goToRevision}
                    className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid={`request-revision-${c.id}`}
                >
                    <ArrowUUpLeft size={14} className="mr-1.5" /> Request revision
                </Button>
            </div>
        </div>
    );
}

